import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, Shield, Loader2, MapPin, Edit3, Eye, Plus,
  User, Settings, Star, BookOpen, Flame, Camera, ChevronRight,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/Layout";
import { ROUTE_PATHS } from "@/lib/index";
import { springPresets } from "@/lib/motion";

/* ─── typy ───────────────────────────────────── */
interface Profile {
  first_name: string;
  last_name: string;
  plan: string;
  storage_used: number;
  created_at: string;
  avatar?: string;
}
interface GraveRow {
  id: string;
  first_name: string;
  last_name: string;
  birth_year: string;
  death_year: string;
  epitaph: string;
  theme_id: string;
  plan: string;
  plot_row: number;
  plot_col: number;
  is_published: boolean;
  created_at: string;
}

/* ─── stałe ──────────────────────────────────── */
const PLAN_LABELS: Record<string, { label: string; color: string; storage: string; glow: string }> = {
  free:       { label: "Free",       color: "rgba(130,150,175,0.9)",  storage: "500 MB", glow: "rgba(130,150,175,0.15)" },
  podstawowy: { label: "Podstawowy", color: "rgba(100,180,220,0.95)", storage: "5 GB",   glow: "rgba(100,180,220,0.15)" },
  premium:    { label: "Premium",    color: "rgba(212,175,55,0.95)",  storage: "50 GB",  glow: "rgba(212,175,55,0.2)"  },
};

const ALLEY_NAMES: Record<number, string> = {
  0: "Aleja św. Piotra",       1: "Aleja Wiecznej Pamięci",
  2: "Aleja Różana",           3: "Aleja Spokoju",
  4: "Aleja Wiary i Nadziei",  5: "Aleja Matki Boskiej",
  6: "Aleja Srebrna",          7: "Aleja Świętych",
  8: "Aleja Złotego Liścia",   9: "Aleja Zmierzchu",
  10: "Aleja Ciszy",           11: "Aleja Anioła Stróża",
  12: "Aleja Gwiazd",          13: "Aleja Modlitwy",
};

function formatBytes(bytes: number) {
  if (bytes < 1024)               return `${bytes} B`;
  if (bytes < 1024 * 1024)        return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/* ─── SVG nagrobek do wyświetlenia w centrum ─── */
function BigTombstone({ plan, themeId, publishedCount }: { plan: string; themeId: string; publishedCount: number }) {
  const isPremium = plan === "premium";
  const isBase = plan === "podstawowy";
  const stroke = isPremium
    ? "rgba(212,175,55,0.85)"
    : isBase ? "rgba(100,180,220,0.7)" : "rgba(140,165,195,0.55)";
  const fill = isPremium ? "#1c1a10" : isBase ? "#0b1520" : "#0e1318";
  const glow = isPremium
    ? "drop-shadow(0 0 18px rgba(212,175,55,0.45)) drop-shadow(0 0 40px rgba(212,175,55,0.2))"
    : isBase ? "drop-shadow(0 0 16px rgba(100,180,220,0.35))"
    : "drop-shadow(0 0 12px rgba(140,165,195,0.2))";

  // wybierz kształt wg themeId
  const shapes: Record<string, JSX.Element> = {
    gothic: (
      <svg viewBox="0 0 80 120" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", filter: glow }}>
        {/* cień */}
        <ellipse cx="40" cy="118" rx="28" ry="4" fill="rgba(0,0,0,0.5)" />
        {/* gotycki łuk */}
        <path d="M8 120V52 L40 8 L72 52 V120 Z" fill={fill} stroke={stroke} strokeWidth="1.5"/>
        {/* okno rozeta */}
        <circle cx="40" cy="38" r="14" fill="none" stroke={stroke} strokeWidth="1" opacity="0.7"/>
        <circle cx="40" cy="38" r="9" fill="none" stroke={stroke} strokeWidth="0.7" opacity="0.5"/>
        <circle cx="40" cy="38" r="3" fill={stroke} opacity="0.5"/>
        {[0,60,120,180,240,300].map(deg => {
          const rad = deg * Math.PI / 180;
          return <line key={deg} x1="40" y1="38" x2={40 + 14*Math.cos(rad)} y2={38 + 14*Math.sin(rad)} stroke={stroke} strokeWidth="0.6" opacity="0.4"/>;
        })}
        {/* inskrypcja */}
        <line x1="18" y1="76" x2="62" y2="76" stroke={stroke} strokeWidth="0.8" opacity="0.5"/>
        <line x1="20" y1="84" x2="60" y2="84" stroke={stroke} strokeWidth="0.6" opacity="0.35"/>
        <line x1="22" y1="92" x2="58" y2="92" stroke={stroke} strokeWidth="0.5" opacity="0.25"/>
        {/* płomień znicza jeśli opublikowany */}
        {publishedCount > 0 && <>
          <ellipse cx="40" cy="105" rx="4" ry="6" fill="rgba(212,175,55,0.25)" stroke="rgba(212,175,55,0.5)" strokeWidth="0.8"/>
          <ellipse cx="40" cy="103" rx="2" ry="3" fill="rgba(240,180,50,0.7)"/>
        </>}
        {/* podstawa */}
        <rect x="4" y="116" width="72" height="4" rx="1.5" fill={fill} stroke={stroke} strokeWidth="1"/>
      </svg>
    ),
    default: (
      <svg viewBox="0 0 80 120" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", filter: glow }}>
        <ellipse cx="40" cy="118" rx="28" ry="4" fill="rgba(0,0,0,0.5)" />
        <path d="M8 120V42 Q8 8 40 8 Q72 8 72 42 V120 Z" fill={fill} stroke={stroke} strokeWidth="1.5"/>
        {/* krzyż */}
        <line x1="40" y1="18" x2="40" y2="54" stroke={stroke} strokeWidth="1.6" opacity="0.75"/>
        <line x1="26" y1="32" x2="54" y2="32" stroke={stroke} strokeWidth="1.6" opacity="0.75"/>
        {/* zdobienie */}
        <line x1="18" y1="72" x2="62" y2="72" stroke={stroke} strokeWidth="0.8" opacity="0.5"/>
        <line x1="20" y1="80" x2="60" y2="80" stroke={stroke} strokeWidth="0.6" opacity="0.35"/>
        <line x1="22" y1="88" x2="58" y2="88" stroke={stroke} strokeWidth="0.5" opacity="0.25"/>
        <line x1="22" y1="96" x2="58" y2="96" stroke={stroke} strokeWidth="0.4" opacity="0.15"/>
        {publishedCount > 0 && <>
          <ellipse cx="40" cy="107" rx="4" ry="6" fill="rgba(212,175,55,0.25)" stroke="rgba(212,175,55,0.5)" strokeWidth="0.8"/>
          <ellipse cx="40" cy="105" rx="2" ry="3" fill="rgba(240,180,50,0.7)"/>
        </>}
        <rect x="4" y="116" width="72" height="4" rx="1.5" fill={fill} stroke={stroke} strokeWidth="1"/>
        {isPremium && <>
          <circle cx="40" cy="8" r="4" fill="rgba(212,175,55,0.15)" stroke="rgba(212,175,55,0.6)" strokeWidth="1"/>
          <circle cx="40" cy="8" r="1.8" fill="rgba(212,175,55,0.8)"/>
        </>}
      </svg>
    ),
  };
  const shape = themeId === "gothic" ? shapes.gothic : shapes.default;
  return shape;
}

/* ─── definicje awatarów ──────────────────────── */
const AVATARS: { id: string; label: string; svg: React.ReactNode }[] = [
  { id: "zombie", label: "Zombie", svg: (
    <svg viewBox="0 0 40 48" fill="none" className="w-full h-full">
      <ellipse cx="20" cy="16" rx="11" ry="13" fill="#7ab87a" stroke="#4a8a4a" strokeWidth="1"/>
      <circle cx="15" cy="15" r="3" fill="#fff"/><circle cx="25" cy="15" r="3" fill="#fff"/>
      <circle cx="15" cy="15.5" r="1.5" fill="#c00"/><circle cx="25" cy="15.5" r="1.5" fill="#c00"/>
      <path d="M14 24 Q20 27 26 24" stroke="#4a8a4a" strokeWidth="1" fill="none"/>
      <rect x="16" y="23" width="3" height="3" rx="0.5" fill="#eee" stroke="#4a8a4a" strokeWidth="0.5"/>
      <rect x="21" y="23" width="3" height="3" rx="0.5" fill="#eee" stroke="#4a8a4a" strokeWidth="0.5"/>
      <path d="M9 29 Q20 34 31 29 L29 48 H11 Z" fill="#7ab87a" stroke="#4a8a4a" strokeWidth="1"/>
      <line x1="5" y1="34" x2="12" y2="30" stroke="#7ab87a" strokeWidth="3" strokeLinecap="round"/>
      <line x1="35" y1="34" x2="28" y2="30" stroke="#7ab87a" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  )},
  { id: "skeleton", label: "Szkielet", svg: (
    <svg viewBox="0 0 40 48" fill="none" className="w-full h-full">
      <ellipse cx="20" cy="15" rx="10" ry="12" fill="#e8e0d0" stroke="#b0a890" strokeWidth="1"/>
      <circle cx="15" cy="14" r="3.5" fill="#1a1a2e"/><circle cx="25" cy="14" r="3.5" fill="#1a1a2e"/>
      <path d="M15 22 Q20 20 25 22" stroke="#b0a890" strokeWidth="1" fill="none"/>
      <rect x="17" y="21" width="2.5" height="3" rx="0.4" fill="#e8e0d0" stroke="#b0a890" strokeWidth="0.5"/>
      <rect x="20.5" y="21" width="2.5" height="3" rx="0.4" fill="#e8e0d0" stroke="#b0a890" strokeWidth="0.5"/>
      <path d="M10 28 Q20 32 30 28 L28 48 H12 Z" fill="#e8e0d0" stroke="#b0a890" strokeWidth="1"/>
      <line x1="10" y1="36" x2="30" y2="36" stroke="#b0a890" strokeWidth="0.7" opacity="0.6"/>
      <line x1="20" y1="28" x2="20" y2="48" stroke="#b0a890" strokeWidth="0.7" opacity="0.5"/>
      <line x1="5" y1="33" x2="11" y2="29" stroke="#e8e0d0" strokeWidth="3" strokeLinecap="round"/>
      <line x1="35" y1="33" x2="29" y2="29" stroke="#e8e0d0" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  )},
  { id: "witch", label: "Wiedźma", svg: (
    <svg viewBox="0 0 40 48" fill="none" className="w-full h-full">
      <polygon points="20,2 30,18 10,18" fill="#2a1a3a" stroke="#7a3a9a" strokeWidth="1"/>
      <line x1="20" y1="18" x2="20" y2="22" stroke="#7a3a9a" strokeWidth="2"/>
      <ellipse cx="20" cy="16" rx="9" ry="10" fill="#c8a87a" stroke="#9a7a4a" strokeWidth="1"/>
      <circle cx="16" cy="15" r="2.5" fill="#2a1a3a"/><circle cx="24" cy="15" r="2.5" fill="#2a1a3a"/>
      <path d="M15 23 Q20 26 25 23" stroke="#9a7a4a" strokeWidth="1" fill="none"/>
      <path d="M11 27 Q20 32 29 27 L27 48 H13 Z" fill="#2a1a3a" stroke="#7a3a9a" strokeWidth="1"/>
    </svg>
  )},
  { id: "vampire", label: "Wampir", svg: (
    <svg viewBox="0 0 40 48" fill="none" className="w-full h-full">
      <ellipse cx="20" cy="16" rx="10" ry="12" fill="#d0c8e0" stroke="#8060a0" strokeWidth="1"/>
      <circle cx="15" cy="15" r="2.8" fill="#1a0a2a"/><circle cx="25" cy="15" r="2.8" fill="#1a0a2a"/>
      <circle cx="15.3" cy="14.5" r="0.8" fill="#c00"/><circle cx="25.3" cy="14.5" r="0.8" fill="#c00"/>
      <path d="M15 23 Q20 25 25 23" stroke="#8060a0" strokeWidth="1" fill="none"/>
      <polygon points="18,23 20,26 22,23" fill="#fff"/>
      <path d="M10 28 Q20 33 30 28 L28 48 H12 Z" fill="#2a0a3a" stroke="#8060a0" strokeWidth="1"/>
    </svg>
  )},
  { id: "ghost", label: "Duch", svg: (
    <svg viewBox="0 0 40 48" fill="none" className="w-full h-full">
      <path d="M20 4 Q32 4 32 18 L32 44 Q28 40 24 44 Q20 48 16 44 Q12 40 8 44 L8 18 Q8 4 20 4Z" fill="rgba(220,230,255,0.9)" stroke="rgba(150,170,230,0.8)" strokeWidth="1"/>
      <circle cx="15" cy="18" r="3.5" fill="#1a1a3a"/><circle cx="25" cy="18" r="3.5" fill="#1a1a3a"/>
      <circle cx="15.5" cy="17.5" r="1" fill="#7a8aff"/><circle cx="25.5" cy="17.5" r="1" fill="#7a8aff"/>
    </svg>
  )},
  { id: "mummy", label: "Mumia", svg: (
    <svg viewBox="0 0 40 48" fill="none" className="w-full h-full">
      <ellipse cx="20" cy="16" rx="10" ry="12" fill="#c8b87a" stroke="#a09050" strokeWidth="1"/>
      <line x1="10" y1="10" x2="30" y2="10" stroke="#a09050" strokeWidth="1.5" opacity="0.7"/>
      <line x1="10" y1="14" x2="30" y2="14" stroke="#a09050" strokeWidth="1.2" opacity="0.6"/>
      <line x1="10" y1="18" x2="30" y2="18" stroke="#a09050" strokeWidth="1" opacity="0.5"/>
      <ellipse cx="16" cy="14" rx="3.5" ry="2.5" fill="#2a1a0a" opacity="0.8"/>
      <circle cx="16" cy="14" r="1.2" fill="#c00" opacity="0.6"/>
      <path d="M10 28 Q20 33 30 28 L28 48 H12 Z" fill="#c8b87a" stroke="#a09050" strokeWidth="1"/>
    </svg>
  )},
  { id: "demon", label: "Demon", svg: (
    <svg viewBox="0 0 40 48" fill="none" className="w-full h-full">
      <path d="M10 4 L14 12 L10 8" fill="#8a1010" stroke="#c03030" strokeWidth="0.8"/>
      <path d="M30 4 L26 12 L30 8" fill="#8a1010" stroke="#c03030" strokeWidth="0.8"/>
      <ellipse cx="20" cy="17" rx="10" ry="11" fill="#8a2020" stroke="#c04040" strokeWidth="1"/>
      <circle cx="15" cy="16" r="3" fill="#ff6030"/><circle cx="25" cy="16" r="3" fill="#ff6030"/>
      <circle cx="15" cy="16" r="1.2" fill="#1a0000"/><circle cx="25" cy="16" r="1.2" fill="#1a0000"/>
      <path d="M10 29 Q20 34 30 29 L28 48 H12 Z" fill="#8a1010" stroke="#c04040" strokeWidth="1"/>
    </svg>
  )},
  { id: "banshee", label: "Banshee", svg: (
    <svg viewBox="0 0 40 48" fill="none" className="w-full h-full">
      <ellipse cx="20" cy="15" rx="10" ry="12" fill="#b0c8e0" stroke="#6090c0" strokeWidth="1"/>
      <circle cx="15" cy="14" r="3" fill="#1a2a3a"/><circle cx="25" cy="14" r="3" fill="#1a2a3a"/>
      <circle cx="15.5" cy="13.5" r="1.2" fill="#00c0ff" opacity="0.9"/><circle cx="25.5" cy="13.5" r="1.2" fill="#00c0ff" opacity="0.9"/>
      <path d="M12 28 Q20 48 28 28 Z" fill="#b0c8e0" stroke="#6090c0" strokeWidth="1"/>
    </svg>
  )},
  { id: "knight", label: "Rycerz", svg: (
    <svg viewBox="0 0 40 48" fill="none" className="w-full h-full">
      <rect x="10" y="6" width="20" height="22" rx="10" fill="#707080" stroke="#a0a0b0" strokeWidth="1"/>
      <rect x="13" y="14" width="14" height="5" rx="1" fill="#1a1a2a" stroke="#a0a0b0" strokeWidth="0.8"/>
      <circle cx="16" cy="16.5" r="1.5" fill="#c0a000" opacity="0.8"/>
      <circle cx="24" cy="16.5" r="1.5" fill="#c0a000" opacity="0.8"/>
      <path d="M10 28 Q20 33 30 28 L29 48 H11 Z" fill="#707080" stroke="#a0a0b0" strokeWidth="1"/>
    </svg>
  )},
  { id: "angel", label: "Anioł", svg: (
    <svg viewBox="0 0 40 48" fill="none" className="w-full h-full">
      <ellipse cx="20" cy="8" rx="6" ry="3" fill="none" stroke="rgba(255,220,100,0.9)" strokeWidth="1.5"/>
      <ellipse cx="20" cy="16" rx="10" ry="11" fill="#f0e8d8" stroke="#d4c090" strokeWidth="1"/>
      <circle cx="15" cy="15" r="2.8" fill="#2a1a0a"/><circle cx="25" cy="15" r="2.8" fill="#2a1a0a"/>
      <path d="M5 22 Q3 14 8 10 Q12 16 10 26 Z" fill="rgba(240,240,255,0.85)" stroke="rgba(200,210,255,0.7)" strokeWidth="0.8"/>
      <path d="M35 22 Q37 14 32 10 Q28 16 30 26 Z" fill="rgba(240,240,255,0.85)" stroke="rgba(200,210,255,0.7)" strokeWidth="0.8"/>
      <path d="M11 28 Q20 34 29 28 L27 48 H13 Z" fill="#f0e8d8" stroke="#d4c090" strokeWidth="1"/>
    </svg>
  )},
];

/* ─── AvatarPicker ───────────────────────────── */
function AvatarPicker({
  currentAvatar, onSelect, size = "lg",
}: { currentAvatar: string; onSelect: (id: string) => void; size?: "sm" | "lg" }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = AVATARS.find((a) => a.id === currentAvatar) ?? null;

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const handleSelect = async (id: string) => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from("profiles").upsert({ id: user.id, avatar: id }, { onConflict: "id" });
        onSelect(id);
      }
    } finally { setSaving(false); setOpen(false); }
  };

  const wh = size === "lg" ? "w-24 h-24" : "w-16 h-16";
  const inner = size === "lg" ? "w-14 h-14" : "w-9 h-9";

  return (
    <div ref={ref} className="relative flex flex-col items-center gap-1.5">
      <button onClick={() => setOpen(v => !v)} className="relative group" style={{ outline: "none" }}>
        <div className={`${wh} rounded-full flex items-center justify-center transition-all duration-300 overflow-hidden relative`}
          style={{
            background: "oklch(0.11 0.025 230)",
            border: current ? "2.5px solid rgba(212,175,55,0.75)" : "2.5px dashed rgba(120,140,170,0.35)",
            boxShadow: current ? "0 0 20px rgba(212,175,55,0.25), inset 0 0 20px rgba(212,175,55,0.05)" : "none",
          }}>
          {current
            ? <div className={inner}>{current.svg}</div>
            : <User className="w-8 h-8" style={{ color: "rgba(130,150,175,0.35)" }} />}
        </div>
        {/* edit overlay */}
        <div className="absolute inset-0 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          style={{ background: "rgba(6,8,18,0.72)", backdropFilter: "blur(3px)" }}>
          <Camera className="w-5 h-5" style={{ color: "rgba(212,175,55,0.9)" }} />
        </div>
        {/* badge aktywny */}
        {current && (
          <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ background: "rgba(212,175,55,0.9)", border: "2px solid oklch(0.09 0.015 235)" }}>
            <span className="text-[8px] font-bold text-black">✦</span>
          </div>
        )}
      </button>
      <span className="text-[10px]" style={{ color: "rgba(130,150,175,0.55)" }}>
        {saving ? "Zapisuję…" : current ? current.label : "Wybierz awatar"}
      </span>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 8 }}
            transition={{ duration: 0.15 }}
            className="absolute top-28 left-0 z-50 rounded-2xl p-4 shadow-2xl"
            style={{
              background: "oklch(0.10 0.022 220)",
              border: "1px solid oklch(0.22 0.032 220)",
              minWidth: 260,
              boxShadow: "0 24px 64px rgba(0,0,0,0.8), 0 0 40px rgba(212,175,55,0.08)",
            }}>
            <p className="text-[11px] text-center mb-3 font-medium tracking-widest uppercase"
              style={{ color: "rgba(180,195,215,0.6)" }}>Twój awatar</p>
            <div className="grid grid-cols-5 gap-2">
              {AVATARS.map((av) => (
                <button key={av.id} onClick={() => handleSelect(av.id)} title={av.label}
                  className="flex flex-col items-center gap-1 p-1.5 rounded-xl transition-all duration-150 hover:scale-110"
                  style={{
                    background: currentAvatar === av.id ? "rgba(212,175,55,0.15)" : "rgba(255,255,255,0.03)",
                    border: currentAvatar === av.id ? "1.5px solid rgba(212,175,55,0.6)" : "1.5px solid rgba(255,255,255,0.06)",
                  }}>
                  <div className="w-9 h-9">{av.svg}</div>
                  <span className="text-[8px]" style={{ color: "rgba(150,165,185,0.7)" }}>{av.label}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setOpen(false)} className="mt-3 w-full text-xs py-1.5 rounded-lg transition-all hover:brightness-110"
              style={{ background: "oklch(0.15 0.025 220)", color: "rgba(140,155,175,0.7)", border: "1px solid oklch(0.22 0.03 220)" }}>
              Zamknij
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Karta grobu (mini feed) ────────────────── */
function GraveFeedCard({ grave, onEdit, onVisit }: { grave: GraveRow; onEdit: () => void; onVisit: () => void }) {
  const plotNumber = grave.plot_row * 5 + grave.plot_col + 1;
  const alley = ALLEY_NAMES[grave.plot_row] ?? `Aleja ${grave.plot_row + 1}`;
  const planInfo = PLAN_LABELS[grave.plan ?? "free"] ?? PLAN_LABELS.free;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={springPresets.gentle}
      className="rounded-2xl overflow-hidden"
      style={{
        background: "oklch(0.10 0.022 222)",
        border: `1px solid ${grave.is_published ? "oklch(0.22 0.045 45)" : "oklch(0.16 0.022 220)"}`,
        boxShadow: grave.is_published ? "0 2px 20px rgba(212,175,55,0.07)" : "none",
      }}>
      {/* top bar */}
      <div className="px-4 pt-3.5 pb-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {/* mini nagrobek ikonka */}
          <div className="w-8 h-10 flex-shrink-0 opacity-80">
            <svg viewBox="0 0 28 38" fill="none" style={{ width: "100%", height: "100%" }}>
              <path d="M3 38V16 Q3 3 14 3 Q25 3 25 16 V38 Z" fill="#0e1318" stroke={planInfo.color} strokeWidth="1.2"/>
              <line x1="14" y1="7" x2="14" y2="18" stroke={planInfo.color} strokeWidth="1" opacity="0.6"/>
              <line x1="9" y1="11" x2="19" y2="11" stroke={planInfo.color} strokeWidth="1" opacity="0.6"/>
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold leading-none"
              style={{ fontFamily: "'Playfair Display', serif", color: "rgba(225,215,195,0.95)" }}>
              {grave.first_name || grave.last_name
                ? `${grave.first_name} ${grave.last_name}`.trim()
                : "Bez nazwy"}
            </p>
            <p className="text-[10px] mt-0.5" style={{ color: "rgba(130,148,170,0.65)" }}>
              {grave.birth_year || "?"} – {grave.death_year || "?"}
            </p>
          </div>
        </div>
        <span className="text-[9px] px-2 py-0.5 rounded-full"
          style={{
            background: grave.is_published ? "rgba(212,175,55,0.1)" : "oklch(0.14 0.02 220)",
            color: grave.is_published ? "rgba(212,175,55,0.8)" : "rgba(110,125,145,0.65)",
            border: `1px solid ${grave.is_published ? "rgba(212,175,55,0.2)" : "oklch(0.20 0.025 220)"}`,
          }}>
          {grave.is_published ? "✦ Opublikowany" : "Szkic"}
        </span>
      </div>

      {/* epitafium */}
      {grave.epitaph && (
        <div className="px-4 pb-2">
          <p className="text-xs italic leading-relaxed line-clamp-2"
            style={{ color: "rgba(165,180,200,0.5)", fontFamily: "'Playfair Display', serif" }}>
            „{grave.epitaph}"
          </p>
        </div>
      )}

      {/* bottom actions */}
      <div className="px-4 pb-3 flex items-center justify-between"
        style={{ borderTop: "1px solid oklch(0.14 0.02 220)", paddingTop: "10px", marginTop: "4px" }}>
        <div className="flex items-center gap-1" style={{ color: "rgba(120,138,160,0.55)" }}>
          <MapPin className="w-3 h-3" />
          <span className="text-[10px]">{alley} · #{plotNumber}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={onVisit}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] transition-all hover:brightness-110"
            style={{ background: "oklch(0.13 0.022 220)", border: "1px solid oklch(0.20 0.028 220)", color: "rgba(160,178,200,0.7)" }}>
            <Eye className="w-3 h-3" /> Plac
          </button>
          <button onClick={onEdit}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] transition-all hover:brightness-110"
            style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.22)", color: "rgba(212,175,55,0.8)" }}>
            <Edit3 className="w-3 h-3" /> Edytuj
          </button>
        </div>
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════
   Główna strona konta
══════════════════════════════════════════════════ */
export default function AccountPage() {
  const navigate = useNavigate();
  const [profile, setProfile]   = useState<Profile | null>(null);
  const [graves, setGraves]     = useState<GraveRow[]>([]);
  const [email, setEmail]       = useState("");
  const [loading, setLoading]   = useState(true);
  const [avatar, setAvatar]     = useState("");
  const [activeTab, setActiveTab] = useState<"groby" | "info">("groby");

  const loadData = useCallback(async (uid: string) => {
    const { data: prof } = await supabase.from("profiles").select("*").eq("id", uid).maybeSingle();
    if (prof) { setProfile(prof as Profile); setAvatar((prof as Profile).avatar ?? ""); }

    const { data: graveData } = await supabase
      .from("graves")
      .select("id, first_name, last_name, birth_year, death_year, epitaph, theme_id, plan, plot_row, plot_col, is_published, created_at")
      .eq("user_id", uid)
      .order("created_at", { ascending: false });
    if (graveData) setGraves(graveData as GraveRow[]);
  }, []);

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    async function init() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate(ROUTE_PATHS.LOGOWANIE, { replace: true }); return; }
      setEmail(user.email ?? "");
      await loadData(user.id);
      setLoading(false);
      channel = supabase.channel(`graves_user_${user.id}`)
        .on("postgres_changes", { event: "*", schema: "public", table: "graves", filter: `user_id=eq.${user.id}` },
          () => loadData(user.id))
        .subscribe();
    }
    init();
    return () => { if (channel) supabase.removeChannel(channel); };
  }, [navigate, loadData]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate(ROUTE_PATHS.HOME, { replace: true });
  };

  const goToPlot = (g: GraveRow) => {
    const pageIdx = Math.floor(g.plot_row / 5);
    navigate(`${ROUTE_PATHS.CMENTARZ}?highlight=${g.plot_row}-${g.plot_col}&page=${pageIdx}`);
  };
  const goToEditor = (g: GraveRow) => {
    navigate(`${ROUTE_PATHS.KREATOR}?plot=empty-${g.plot_row}-${g.plot_col}&plan=${g.plan ?? "free"}`);
  };

  if (loading) return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "rgba(212,175,55,0.5)" }} />
      </div>
    </Layout>
  );

  const planInfo  = PLAN_LABELS[profile?.plan ?? "free"] ?? PLAN_LABELS.free;
  const published = graves.filter(g => g.is_published).length;
  const displayName = profile?.first_name && profile?.last_name
    ? `${profile.first_name} ${profile.last_name}`
    : email.split("@")[0];
  const primaryGrave = graves[0] ?? null;

  return (
    <Layout>
      <div className="min-h-screen" style={{ background: "linear-gradient(180deg, oklch(0.07 0.012 235) 0%, oklch(0.10 0.022 225) 100%)" }}>

        {/* ══ COVER / HERO BANNER ══ */}
        <div className="relative h-40 md:h-52 overflow-hidden"
          style={{ background: "linear-gradient(135deg, oklch(0.09 0.03 240), oklch(0.06 0.015 260), oklch(0.10 0.04 45))" }}>
          {/* gwiazdki tło */}
          {Array.from({ length: 32 }).map((_, i) => (
            <div key={i}
              className="absolute rounded-full"
              style={{
                width: i % 4 === 0 ? 2 : 1,
                height: i % 4 === 0 ? 2 : 1,
                left: `${(i * 37 + 11) % 100}%`,
                top: `${(i * 23 + 7) % 100}%`,
                background: "rgba(212,175,55,0.6)",
                opacity: 0.2 + (i % 5) * 0.15,
              }} />
          ))}
          {/* gradient overlay na dole */}
          <div className="absolute inset-x-0 bottom-0 h-24"
            style={{ background: "linear-gradient(to top, oklch(0.07 0.012 235), transparent)" }} />
          {/* przycisk wylogowania – prawy górny */}
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <Link to={ROUTE_PATHS.WERSJE}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs transition-all hover:brightness-110"
              style={{ background: "rgba(6,8,22,0.6)", border: "1px solid rgba(212,175,55,0.15)", color: "rgba(180,195,215,0.6)", backdropFilter: "blur(8px)" }}>
              <BookOpen className="w-3.5 h-3.5" /> Wersje
            </Link>
            <button onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs transition-all hover:brightness-110"
              style={{ background: "rgba(6,8,22,0.6)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(160,175,195,0.65)", backdropFilter: "blur(8px)" }}>
              <LogOut className="w-3.5 h-3.5" /> Wyloguj
            </button>
          </div>
        </div>

        {/* ══ PROFIL ROW ══ */}
        <div className="container mx-auto max-w-4xl px-4">
          <div className="relative flex flex-col md:flex-row md:items-end gap-4 -mt-12 md:-mt-16 mb-6">

            {/* AWATAR – lewy górny, wychodzi poza cover */}
            <div className="flex-shrink-0 z-10">
              <div className="rounded-full p-1"
                style={{ background: "oklch(0.07 0.012 235)", border: "3px solid oklch(0.07 0.012 235)" }}>
                <AvatarPicker currentAvatar={avatar} onSelect={(id) => setAvatar(id)} size="lg" />
              </div>
            </div>

            {/* DANE PROFILU */}
            <div className="flex-1 pb-1">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold leading-none mb-1"
                    style={{ fontFamily: "'Playfair Display', serif", color: "rgba(230,218,195,0.95)" }}>
                    {displayName}
                  </h1>
                  <p className="text-sm" style={{ color: "rgba(130,148,170,0.6)" }}>{email}</p>
                </div>
                {/* statystyki inline jak IG */}
                <div className="flex items-center gap-5">
                  <div className="text-center">
                    <p className="text-lg font-bold leading-none" style={{ color: "rgba(212,175,55,0.9)" }}>{graves.length}</p>
                    <p className="text-[10px] mt-0.5 uppercase tracking-wider" style={{ color: "rgba(130,148,170,0.55)" }}>grobów</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold leading-none" style={{ color: "rgba(212,175,55,0.9)" }}>{published}</p>
                    <p className="text-[10px] mt-0.5 uppercase tracking-wider" style={{ color: "rgba(130,148,170,0.55)" }}>opubl.</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold leading-none" style={{ color: planInfo.color }}>{planInfo.label}</p>
                    <p className="text-[10px] mt-0.5 uppercase tracking-wider" style={{ color: "rgba(130,148,170,0.55)" }}>plan</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ══ GŁÓWNA SEKCJA 2-kolumna ══ */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 pb-20">

            {/* ── LEWA: duży nagrobek + akcje ── */}
            <div className="md:col-span-2 flex flex-col gap-4">

              {/* Card nagrobka */}
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={springPresets.gentle}
                className="rounded-3xl overflow-hidden relative"
                style={{
                  background: "linear-gradient(160deg, oklch(0.11 0.03 225), oklch(0.08 0.018 240))",
                  border: "1px solid oklch(0.20 0.04 45)",
                  boxShadow: "0 0 40px rgba(212,175,55,0.08), 0 8px 32px rgba(0,0,0,0.5)",
                  minHeight: 340,
                }}>

                {/* tło – mgiełka */}
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "radial-gradient(ellipse at 50% 80%, rgba(212,175,55,0.06) 0%, transparent 70%)" }} />

                {/* górny pasek z planem */}
                <div className="relative flex items-center justify-between px-5 pt-4 pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: planInfo.color, boxShadow: `0 0 6px ${planInfo.color}` }} />
                    <span className="text-[10px] uppercase tracking-[0.25em] font-medium" style={{ color: planInfo.color }}>
                      {planInfo.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {primaryGrave && (
                      <span className="text-[10px]" style={{ color: "rgba(130,148,170,0.45)" }}>
                        {primaryGrave.is_published ? "✦ Opublikowany" : "Szkic"}
                      </span>
                    )}
                    <Link to={`${ROUTE_PATHS.KREATOR}${primaryGrave ? `?plot=empty-${primaryGrave.plot_row}-${primaryGrave.plot_col}&plan=${primaryGrave.plan}&mode=design` : ""}`}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] transition-all hover:brightness-110"
                      style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.25)", color: "rgba(212,175,55,0.85)" }}>
                      <Settings className="w-3 h-3" /> Personalizuj
                    </Link>
                  </div>
                </div>

                {/* DUŻY NAGROBEK w centrum */}
                <div className="flex items-end justify-center pt-6 pb-4 relative" style={{ minHeight: 220 }}>
                  {/* podłoga / ziemia */}
                  <div className="absolute bottom-0 left-0 right-0 h-12"
                    style={{ background: "linear-gradient(to top, rgba(8,12,20,0.8), transparent)" }} />
                  {/* trawa dekoracyjna */}
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1 opacity-30">
                    {Array.from({ length: 18 }).map((_, i) => (
                      <div key={i} style={{
                        width: 1.5, height: 6 + (i % 3) * 3,
                        background: "rgba(60,120,60,0.8)",
                        borderRadius: 2,
                        marginBottom: i % 2 === 0 ? 0 : 3,
                      }} />
                    ))}
                  </div>

                  {primaryGrave ? (
                    <motion.div
                      animate={{ y: [0, -4, 0] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                      className="relative z-10 cursor-pointer"
                      style={{ width: 130, height: 190 }}
                      onClick={() => goToEditor(primaryGrave)}>
                      <BigTombstone plan={primaryGrave.plan} themeId={primaryGrave.theme_id} publishedCount={published} />
                      {/* hover hint */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200 rounded-sm"
                        style={{ background: "rgba(6,8,18,0.5)", backdropFilter: "blur(2px)" }}>
                        <div className="flex flex-col items-center gap-1">
                          <Edit3 className="w-5 h-5" style={{ color: "rgba(212,175,55,0.9)" }} />
                          <span className="text-[10px]" style={{ color: "rgba(212,175,55,0.8)" }}>Edytuj</span>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <Link to={ROUTE_PATHS.CMENTARZ}
                      className="relative z-10 flex flex-col items-center gap-3 group"
                      style={{ width: 120, height: 180 }}>
                      <div className="w-full h-full rounded-xl flex flex-col items-center justify-center gap-3 transition-all"
                        style={{ border: "2px dashed oklch(0.28 0.035 220)", background: "oklch(0.09 0.018 230)" }}>
                        <Plus className="w-8 h-8 opacity-30 group-hover:opacity-60 transition-opacity" style={{ color: "rgba(212,175,55,0.8)" }} />
                        <span className="text-[10px] text-center uppercase tracking-wider opacity-40 group-hover:opacity-70 transition-opacity"
                          style={{ color: "rgba(180,200,220,0.8)" }}>Zarezerwuj<br/>grób</span>
                      </div>
                    </Link>
                  )}
                </div>

                {/* imię pod nagrobkiem */}
                {primaryGrave && (
                  <div className="text-center px-4 pb-5">
                    <p className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(215,200,175,0.85)" }}>
                      {primaryGrave.first_name || primaryGrave.last_name
                        ? `${primaryGrave.first_name} ${primaryGrave.last_name}`.trim() : "Bez nazwy"}
                    </p>
                    {(primaryGrave.birth_year || primaryGrave.death_year) && (
                      <p className="text-[11px] mt-0.5" style={{ color: "rgba(130,148,170,0.55)" }}>
                        {primaryGrave.birth_year || "?"} – {primaryGrave.death_year || "?"}
                      </p>
                    )}
                  </div>
                )}
              </motion.div>

              {/* Storage bar */}
              <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ ...springPresets.gentle, delay: 0.1 }}
                className="rounded-2xl p-4"
                style={{ background: "oklch(0.10 0.022 222)", border: "1px solid oklch(0.18 0.025 220)" }}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[11px] uppercase tracking-wider" style={{ color: "rgba(130,148,170,0.55)" }}>Przestrzeń dyskowa</span>
                  <span className="text-[11px]" style={{ color: planInfo.color }}>
                    {formatBytes(profile?.storage_used ?? 0)} / {planInfo.storage}
                  </span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "oklch(0.15 0.025 220)" }}>
                  <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: "2%" }}
                    transition={{ duration: 1, delay: 0.5 }}
                    style={{ background: `linear-gradient(to right, ${planInfo.color}, ${planInfo.color}99)` }} />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5" style={{ color: planInfo.color }} />
                    <span className="text-xs" style={{ color: planInfo.color }}>{planInfo.label}</span>
                  </div>
                  <Link to={ROUTE_PATHS.HOME + "#plany"}
                    className="text-[11px] flex items-center gap-1 transition-opacity hover:opacity-80"
                    style={{ color: "rgba(130,148,170,0.55)" }}>
                    Zmień plan <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* ── PRAWA: feed grobów ── */}
            <div className="md:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ ...springPresets.gentle, delay: 0.12 }}>

                {/* tabs */}
                <div className="flex items-center gap-1 mb-4 p-1 rounded-2xl"
                  style={{ background: "oklch(0.10 0.022 222)", border: "1px solid oklch(0.16 0.022 220)" }}>
                  {([["groby", "Moje e-groby", graves.length], ["info", "Informacje", null]] as const).map(([tab, label, count]) => (
                    <button key={tab} onClick={() => setActiveTab(tab as "groby" | "info")}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-[12px] font-medium transition-all duration-200"
                      style={{
                        background: activeTab === tab ? "rgba(212,175,55,0.1)" : "transparent",
                        color: activeTab === tab ? "rgba(212,175,55,0.9)" : "rgba(130,148,170,0.55)",
                        border: activeTab === tab ? "1px solid rgba(212,175,55,0.2)" : "1px solid transparent",
                      }}>
                      {label}
                      {count !== null && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full"
                          style={{ background: activeTab === tab ? "rgba(212,175,55,0.15)" : "rgba(255,255,255,0.05)", color: "inherit" }}>
                          {count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* tab: groby */}
                {activeTab === "groby" && (
                  <>
                    {graves.length === 0 ? (
                      <div className="rounded-2xl px-6 py-16 text-center"
                        style={{ background: "oklch(0.10 0.022 220)", border: "1px dashed oklch(0.20 0.025 220)" }}>
                        <div className="flex justify-center mb-4 opacity-20">
                          <svg width="48" height="64" viewBox="0 0 56 78" fill="none">
                            <path d="M6 78V30 Q6 6 28 6 Q50 6 50 30 V78 Z" fill="none" stroke="rgba(212,175,55,0.7)" strokeWidth="1.5"/>
                            <line x1="28" y1="14" x2="28" y2="34" stroke="rgba(212,175,55,0.7)" strokeWidth="1.5"/>
                            <line x1="18" y1="22" x2="38" y2="22" stroke="rgba(212,175,55,0.7)" strokeWidth="1.5"/>
                          </svg>
                        </div>
                        <p className="text-sm mb-5" style={{ color: "rgba(130,148,170,0.6)" }}>Nie masz jeszcze żadnego e-grobu.</p>
                        <Link to={ROUTE_PATHS.CMENTARZ}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all hover:brightness-110"
                          style={{ background: "rgba(212,175,55,0.85)", color: "rgba(6,8,18,0.95)" }}>
                          Zarezerwuj miejsce <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <AnimatePresence>
                          {graves.map((g) => (
                            <GraveFeedCard key={g.id} grave={g} onEdit={() => goToEditor(g)} onVisit={() => goToPlot(g)} />
                          ))}
                        </AnimatePresence>
                        {/* dodaj kolejny */}
                        <Link to={ROUTE_PATHS.CMENTARZ}
                          className="flex items-center justify-center gap-2 py-4 rounded-2xl text-sm transition-all hover:brightness-110"
                          style={{ background: "oklch(0.10 0.022 222)", border: "1.5px dashed oklch(0.22 0.03 220)", color: "rgba(130,148,170,0.5)" }}>
                          <Plus className="w-4 h-4" /> Zarezerwuj kolejne miejsce
                        </Link>
                      </div>
                    )}
                  </>
                )}

                {/* tab: info */}
                {activeTab === "info" && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                    {[
                      { label: "E-mail", value: email, icon: <User className="w-4 h-4" /> },
                      { label: "Plan", value: planInfo.label, icon: <Star className="w-4 h-4" /> },
                      { label: "Konto od", value: profile?.created_at ? new Date(profile.created_at).toLocaleDateString("pl-PL") : "–", icon: <BookOpen className="w-4 h-4" /> },
                      { label: "Opublikowanych grobów", value: `${published} / ${graves.length}`, icon: <Flame className="w-4 h-4" /> },
                    ].map(({ label, value, icon }) => (
                      <div key={label} className="flex items-center gap-3 px-5 py-3.5 rounded-2xl"
                        style={{ background: "oklch(0.10 0.022 222)", border: "1px solid oklch(0.16 0.022 220)" }}>
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ background: "rgba(212,175,55,0.08)", color: "rgba(212,175,55,0.55)" }}>
                          {icon}
                        </div>
                        <div className="flex-1 flex items-center justify-between">
                          <span className="text-[11px] uppercase tracking-wider" style={{ color: "rgba(120,138,160,0.55)" }}>{label}</span>
                          <span className="text-sm font-medium" style={{ color: "rgba(215,205,185,0.85)" }}>{value}</span>
                        </div>
                      </div>
                    ))}

                    {/* CTA upgrade jeśli free */}
                    {(profile?.plan === "free" || !profile?.plan) && (
                      <Link to={ROUTE_PATHS.HOME + "#plany"}
                        className="flex items-center justify-between px-5 py-4 rounded-2xl transition-all hover:brightness-110"
                        style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.2)" }}>
                        <div>
                          <p className="text-sm font-semibold" style={{ color: "rgba(212,175,55,0.9)" }}>Przejdź na Premium</p>
                          <p className="text-[11px] mt-0.5" style={{ color: "rgba(180,165,120,0.6)" }}>50 GB · Złoty awatar · Priorytet</p>
                        </div>
                        <div className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-[11px]"
                          style={{ background: "rgba(212,175,55,0.85)", color: "rgba(6,8,18,0.9)" }}>
                          Upgrade <ChevronRight className="w-3.5 h-3.5" />
                        </div>
                      </Link>
                    )}
                  </motion.div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
